import React, { useState, memo, useMemo, useCallback } from 'react'
import { Input, Dropdown, Button, Menu, Icon, Tooltip, Modal } from 'antd'
import SceneTransition from './SceneTransition'
import Shot from './Shot'
import {
  SortableContainer,
  SortableElement,
  SortableHandle
} from 'react-sortable-hoc'

const { TextArea } = Input

// Memoize the DragHandle
const DragHandle = memo(
  SortableHandle(() => (
    <span className="drag-handle" style={{ cursor: 'grab', marginRight: 8 }}>
      <Icon type="drag" />
    </span>
  ))
)

// Memoize individual Shot wrapper - BUT allow prompt updates
const SortableShot = memo(
  SortableElement(
    ({
      shot,
      displayIndex,
      sceneId,
      formData,
      setScenes,
      videoGenerating,
      transitions,
      handleRegenerateVideo,
      handleUploadVideo,
      handleEditSettings,
      handleTransitionSelect,
      isGeneratingAll,
      videoProvider,
      handleDeleteShot,
      updateShotDataRef,
      // Image generation props
      handleRegenerateImage,
      handleRegenerateWithReferenceNanoBanana,
      isImageGenerationLoading,
      videoId
    }) => (
      <Shot
        shot={shot}
        sceneId={sceneId}
        index={displayIndex}
        isLastShot={
          displayIndex ===
          formData.shotList.find(s => s.id === sceneId)?.shots.length - 1
        }
        formData={formData}
        setScenes={setScenes}
        videoGenerating={videoGenerating}
        transitions={transitions}
        handleRegenerateVideo={handleRegenerateVideo}
        handleUploadVideo={handleUploadVideo}
        handleEditSettings={handleEditSettings}
        handleTransitionSelect={handleTransitionSelect}
        isGeneratingAll={isGeneratingAll}
        videoProvider={videoProvider}
        handleDeleteShot={handleDeleteShot}
        DragHandle={DragHandle}
        updateShotDataRef={updateShotDataRef}
        // Image generation props
        handleRegenerateImage={handleRegenerateImage}
        handleRegenerateWithReferenceNanoBanana={
          handleRegenerateWithReferenceNanoBanana
        }
        isImageGenerationLoading={isImageGenerationLoading}
        videoId={videoId}
      />
    )
  ),
  (prevProps, nextProps) => {
    // Allow prompt updates, but still optimize other re-renders
    // Check if this specific shot's critical data changed
    const criticalDataChanged =
      prevProps.shot.vidSrc !== nextProps.shot.vidSrc ||
      prevProps.shot.imageSrc !== nextProps.shot.imageSrc ||
      prevProps.shot.errorGeneratingVideo !==
        nextProps.shot.errorGeneratingVideo ||
      prevProps.shot.progress !== nextProps.shot.progress ||
      prevProps.displayIndex !== nextProps.displayIndex ||
      prevProps.isGeneratingAll !== nextProps.isGeneratingAll

    // Check if prompts changed for THIS shot
    const promptsChanged =
      prevProps.shot.video_prompt !== nextProps.shot.video_prompt ||
      prevProps.shot.initial_image_prompt !==
        nextProps.shot.initial_image_prompt ||
      prevProps.shot.negative_prompt !== nextProps.shot.negative_prompt

    // Check if video generation status changed for THIS shot
    const prevGenerating = prevProps.videoGenerating.some(
      v => v.sceneId === prevProps.sceneId && v.shotId === prevProps.shot.id
    )
    const nextGenerating = nextProps.videoGenerating.some(
      v => v.sceneId === nextProps.sceneId && v.shotId === nextProps.shot.id
    )
    const generatingChanged = prevGenerating !== nextGenerating

    const transitionChanged = prevProps.transitions !== nextProps.transitions

    // Check if relevant formData changed
    const formDataChanged =
      prevProps.formData.characterList !== nextProps.formData.characterList ||
      prevProps.formData.locationReference !==
        nextProps.formData.locationReference ||
      prevProps.formData.selectedCharacter !==
        nextProps.formData.selectedCharacter ||
      prevProps.formData.locationMap !== nextProps.formData.locationMap

    // Re-render if critical data, prompts, or generation status changed
    // Return true = skip render, false = do render
    return (
      !criticalDataChanged &&
      !promptsChanged &&
      !generatingChanged &&
      !transitionChanged &&
      !formDataChanged
    )
  }
)

const SortableShotList = SortableContainer(
  ({
    shots,
    sceneId,
    formData,
    setScenes,
    videoGenerating,
    transitions,
    handleRegenerateVideo,
    handleUploadVideo,
    handleEditSettings,
    handleTransitionSelect,
    isGeneratingAll,
    videoProvider,
    handleDeleteShot,
    updateShotDataRef,
    // Image generation props
    handleRegenerateImage,
    handleRegenerateWithReferenceNanoBanana,
    isImageGenerationLoading,
    videoId
  }) => {
    return (
      <div className="shots-list">
        {shots.map((shot, index) => (
          <SortableShot
            key={shot.id}
            index={index}
            displayIndex={index}
            shot={shot}
            sceneId={sceneId}
            formData={formData}
            setScenes={setScenes}
            videoGenerating={videoGenerating}
            transitions={transitions}
            handleRegenerateVideo={handleRegenerateVideo}
            handleUploadVideo={handleUploadVideo}
            handleEditSettings={handleEditSettings}
            handleTransitionSelect={handleTransitionSelect}
            isGeneratingAll={isGeneratingAll}
            videoProvider={videoProvider}
            handleDeleteShot={handleDeleteShot}
            updateShotDataRef={updateShotDataRef}
            // Image generation props
            handleRegenerateImage={handleRegenerateImage}
            handleRegenerateWithReferenceNanoBanana={
              handleRegenerateWithReferenceNanoBanana
            }
            isImageGenerationLoading={isImageGenerationLoading}
            videoId={videoId}
          />
        ))}
      </div>
    )
  }
)

// Scene component abstracts a complete scene with its shots
const Scene = ({
  scene,
  transitions,
  formData,
  setScenes,
  isGeneratingAll = false,
  videoGenerating = [],
  handleRegenerateVideo,
  handleUploadVideo,
  handleEditSettings,
  handleTransitionSelect,
  videoProvider,
  onSortEnd,
  onPreviewScene,
  updateShotDataRef,
  handleDeleteShot,
  // Image generation props
  handleRegenerateImage,
  handleRegenerateWithReferenceNanoBanana,
  isImageGenerationLoading,
  videoId
}) => {
  const lastScene = formData.shotList[formData.shotList.length - 1]
  const isLastScene = lastScene && lastScene.id === scene.id

  const firstScene = formData.shotList[0]
  const isFirstScene = firstScene && firstScene.id === scene.id

  // canPreview: all shots must have vidSrc
  const canPreview = useMemo(() => {
    return (
      scene.shots &&
      scene.shots.length > 0 &&
      scene.shots.every(shot => shot && shot.vidSrc)
    )
  }, [scene.shots.map(s => s?.vidSrc).join(',')])

  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false)

  const handleConfirmPreview = useCallback(() => {
    onPreviewScene && onPreviewScene(scene.id)
    setIsPreviewModalOpen(false)
  }, [onPreviewScene, scene.id])

  const handlePreviewClick = useCallback(() => {
    if (!canPreview) {
      Modal.info({
        title: 'Cannot preview scene',
        content:
          'All shots must have a video source to preview. Please upload or generate videos for each shot.'
      })
      return
    }

    setIsPreviewModalOpen(true)
  }, [canPreview])

  return (
    <>
      <div className="scene-container">
        <div className="left-panel">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <h2 style={{ margin: 0, marginRight: 8 }}>
                {scene.title} ({scene.shots && scene.shots.length} shots)
              </h2>

              <Tooltip
                title={
                  scene.shots.length < 2
                    ? 'Scene must have at least 2 shots to preview.'
                    : isGeneratingAll || videoGenerating.includes(scene.id)
                    ? 'Scene is currently generating'
                    : canPreview
                    ? 'Preview scene'
                    : 'All shots must have a video source to preview'
                }
              >
                <Button
                  type="primary"
                  size="small"
                  onClick={handlePreviewClick}
                  disabled={
                    !canPreview ||
                    isGeneratingAll ||
                    videoGenerating.includes(scene.id) ||
                    scene.shots.length < 2
                  }
                  style={{
                    height: 32,
                    marginLeft: 4,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <Icon type="eye" />
                  <span style={{ marginLeft: 6, fontSize: 12 }}>Preview</span>
                </Button>
              </Tooltip>
            </div>
          </div>
          <TextArea
            value={scene.description}
            rows={2}
            disabled={isGeneratingAll || videoGenerating.includes(scene.id)}
            onChange={e => {
              const newDescription = e.target.value
              setScenes(prevScenes =>
                prevScenes.map(s =>
                  s.id === scene.id ? { ...s, description: newDescription } : s
                )
              )
            }}
          />
        </div>

        <div className="right-panel">
          <div className="shots-grid">
            {isFirstScene && (
              <SceneTransition
                sceneId={scene.id}
                position="start"
                transitionType={transitions[`${scene.id}-start`]}
                handleTransitionSelect={handleTransitionSelect}
              />
            )}
            <SortableShotList
              axis="x"
              shots={scene.shots}
              sceneId={scene.id}
              formData={formData}
              setScenes={setScenes}
              videoGenerating={videoGenerating}
              transitions={transitions}
              handleRegenerateVideo={handleRegenerateVideo}
              handleUploadVideo={handleUploadVideo}
              handleEditSettings={handleEditSettings}
              handleTransitionSelect={handleTransitionSelect}
              isGeneratingAll={isGeneratingAll}
              videoProvider={videoProvider}
              updateShotDataRef={updateShotDataRef}
              handleDeleteShot={handleDeleteShot}
              // Image generation props
              handleRegenerateImage={handleRegenerateImage}
              handleRegenerateWithReferenceNanoBanana={
                handleRegenerateWithReferenceNanoBanana
              }
              isImageGenerationLoading={isImageGenerationLoading}
              videoId={videoId}
              useDragHandle
              onSortEnd={onSortEnd}
              distance={1}
              helperClass="sortable-helper"
            />

            {/* <SceneTransition
              sceneId={scene.id}
              position="end"
              transitionType={transitions[`${scene.id}-end`]}
              handleTransitionSelect={handleTransitionSelect}
            /> */}
          </div>
        </div>
      </div>

      <Modal
        title="Confirm Scene Preview"
        visible={isPreviewModalOpen}
        onOk={handleConfirmPreview}
        onCancel={() => setIsPreviewModalOpen(false)}
        okText="Continue"
        cancelText="Cancel"
        centered
      >
        <p>
          This will start the preview generation process for this scene. It will
          only contain the shots inside this scene. Are you sure you want to
          proceed?
        </p>
      </Modal>
    </>
  )
}

export default Scene
